// Core Libraries
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

// Custom Hooks and Utilities
import useForm from "../../hooks/useForm";
import { useThunk } from "../../hooks/useThunk";

import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";

// Redux Actions/Thunks
import { resetPassword } from "../../store";

// Components
import CustomInput from "../Form/Input";
import CustomButton from "../Form/CustomButton";
import AuthHeader from "./AuthHeader";
import AuthFooter from "./AuthFooter";

const ForgotPasswordForm = () => {
  const [doResetPassword, isResettingPassword, resetPasswordError] =
    useThunk(resetPassword);
  const [resetMessage, setResetMessage] = useState({ type: '', message: '' }); // Add state for reset messages
  
  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    setFormData,
    clearError,
  } = useForm(
    {
      email: "",
    },
    {
      email: true,
    },
    {
      email: COMMON_FIELD_TYPES.EMAIL_FIELD,
    }
  );
  
  const handleSubmit = async (e) => {
    e.preventDefault();

    setResetMessage({ type: '', message: '' });

    setTouched({
      email: true,
    });

    const isValid =validateForm();

    if(!formData.email || formData.email.trim() === "") {
      setResetMessage({ type: 'error', message: "Please enter either your login or email." });
      return; 
    }

    if (isValid) {
      const result = await doResetPassword(formData);
      if (result?.success) {
        setResetMessage({
          type: 'success',
          message: result?.response?.message || "Password Reset Link Sent To Your Email Successfully"
        });
      } else {
        setResetMessage({
          type: 'error',
          message: result?.error.message || "Failed to send reset link"
        });
      }
    } else {
      // Validation failed - show "Please enter either your login or email." message
      setResetMessage({ 
        type: 'error', 
        message: "The information you have entered is incorrect." 
      });
    }
  };

  return (
    <div className="flex flex-col items-center w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl pt-4 sm:pt-6 md:pt-8 lg:pt-10 pb-3 sm:pb-4 md:pb-5 mt-36">
      
      {/* Logo */}
      <AuthHeader />
    
      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-10 pb-6 sm:pb-8 md:pb-10 rounded-lg shadow-lg bg-[#1A1924] font-public-sans font-bold text-sm sm:text-base md:text-lg leading-none tracking-normal text-center">
        
        {/* Error/Success Message */}
        <div className="h-6 sm:h-7 md:h-8 my-1 sm:my-2 flex items-center justify-center">
          {resetMessage.message && (
            <div
              className={`w-full px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-[10px] sm:text-sm font-medium text-center ${
                resetMessage.type === "success"
                  ? "bg-green-100 text-green-800 border border-green-200"
                  : "bg-red-100 text-red-800 border border-red-200"
              }`}
            >
              {resetMessage.message}
            </div>
          )}
        </div>

        {/* Title*/}
        <h1 className=" text-xl md:text-[1.625rem] font-bold text-[#B3B3B3]">
          Lost Password?
        </h1>

        {/* Verification notice */}
        <div className="pt-4 sm:pt-6 md:pt-8 lg:pt-10 pb-4 sm:pb-6 md:pb-[1.875rem]">
          <p className="font-normal text-[#8D8D8D] text-xs sm:text-sm md:text-base lg:text-lg text-left px-2 sm:px-4 md:px-6 lg:px-10">
            Enter your email address and we will send you a link to reset your password.
          </p>
        </div>

        {/* Form Fields Container*/}
        <div className="space-y-8 w-full max-w-[18rem] sm:max-w-[20rem] md:max-w-[22rem] mx-auto">
          <div className="relative">
            <CustomInput
              type="email"
              name="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              onBlur={() => handleBlur("email", formData.email)}
              onFocus={() => {
                clearError("email");
                setResetMessage({ type: "", message: "" }); // Clear message when user starts typing
              }}
              placeholder="Enter your email address"
              error={false}
              prefixIcon={
                <svg
                  width="16"
                  height="16"
                  className="sm:w-5 sm:h-5"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 0C11.3261 0 12.5979 0.526784 13.5355 1.46447C14.4732 2.40215 15 3.67392 15 5C15 6.32608 14.4732 7.59785 13.5355 8.53553C12.5979 9.47322 11.3261 10 10 10C8.67392 10 7.40215 9.47322 6.46447 8.53553C5.52678 7.59785 5 6.32608 5 5C5 3.67392 5.52678 2.40215 6.46447 1.46447C7.40215 0.526784 8.67392 0 10 0ZM10 12.5C15.525 12.5 20 14.7375 20 17.5V20H0V17.5C0 14.7375 4.475 12.5 10 12.5Z"
                    fill="white"
                  />
                </svg>
              }
              className="h-9 sm:h-10 md:h-11 w-full text-white"
            />
          </div>
          
          {/* Reset Button */}
          <CustomButton
            type="submit"
            buttonText="Reset Password"
            loading={isResettingPassword}
            disabled={isResettingPassword}
            className="mt-3 sm:mt-4 w-full h-9 sm:h-10 md:h-11 text-base sm:text-lg md:text-xl font-bold text-[#1E1E1E]"
            onClick={handleSubmit}
          />
          
          {/* Login Link*/}
          <Link
            to="/signin"
            className="font-normal text-[12px] sm:text-base md:text-lg text-[#8D8D8D] font-public-sans underline mt-3 sm:mt-4 md:mt-5 block"
          >
            Log In
          </Link>
        </div>
      </div>

      {/* Already have account link */}
      <div className="flex justify-center mt-1 sm:mt-2 px-4">
        <p className="font-normal text-[12px] sm:text-base md:text-lg text-[#8D8D8D] font-public-sans text-center">
          Already have an account?{" "}
          <Link
            to="/signin"
            className="font-medium text-[12px] sm:text-base md:text-lg text-[#CECECE] font-public-sans underline"
          >
            Login here
          </Link>
        </p>
      </div>
      
      {/* Footer */}
      <AuthFooter />
    </div>
  );
};

export default ForgotPasswordForm;